// ---------------------------------------------------------------------------
// Test recorder — запись/воспроизведение пользовательских сценариев.
//
// Логика вынесена из mainStore.js, чтобы правки в сторе были минимальны.
// Реактивное состояние живёт в сторе (state.testRecording), сюда передаётся
// `store`. Все функции защищены try/catch — рекордер никогда не должен ломать
// приложение.
//
// Модель: запись привязана к ОТКРЫТОМУ элементу сегмента TEST-SCENARIO
// (кнопка ЗАПИСЬ на нём). События пишутся в record.doc.events этого элемента,
// СТОП сохраняет его штатным save. Хронология рендерится гридом doc.events (BE).
// ---------------------------------------------------------------------------

import { t, tn } from '@/i18n'   // IE 2026 09 23 / ODOO-341407: i18n (тосты рекордера; заголовки событий/шагов и title записи — данные, не переводим)

// --- утилиты -----------------------------------------------------------------

function safe(fn) {
    try { return fn() } catch (e) { console.warn('[testRecorder]', e); return undefined }
}

// все открытые виджеты: глобальные (store.WIDGETS) + виджеты страниц воркспейсов
// (store.WORKSPACES[*].ALL_WIDGETS). Отчёты/записи живут именно в ALL_WIDGETS.
export function allOpenWidgets(store) {
    const res = []
    safe(() => { for (const w of Object.values(store.WIDGETS || {})) if (w && !w.close) res.push(w) })
    safe(() => {
        for (const ws of Object.values(store.WORKSPACES || {})) {
            const aw = ws && typeof ws === 'object' ? ws.ALL_WIDGETS : null
            if (aw) for (const w of Object.values(aw)) if (w && !w.close) res.push(w)
        }
    })
    return res
}

// найти открытый элемент сегмента TEST-SCENARIO (для команд из меню, где нет привязки)
export function findTestWidget(store) {
    return safe(() => allOpenWidgets(store).find(w => w.record?.segment === 'TEST-SCENARIO'))
}

// текущее окружение: process_model / workspace / page из route + открытые виджеты
export function snapshotEnv(store) {
    return safe(() => {
        const hash = window.location.hash || ''
        const qs = hash.includes('?') ? hash.split('?')[1] : ''
        const p = new URLSearchParams(qs)
        const dbMatch = hash.match(/#\/(database_[^/?]+)/)
        const openWidgets = safe(() => allOpenWidgets(store)
            .filter(w => w.id != null)
            .map(w => ({ id: w.id, widget: w.widget, title: w.record?.title || '' }))) || []
        return {
            database: dbMatch ? dbMatch[1] : '',
            process_model: p.get('process_model') || store.process_model_record?.id || '',
            workspace: p.get('workspace') || '',
            page: p.get('page') || '',
            widgets: openWidgets,
        }
    }) || {}
}

function eventType(cmd, method) {
    if (cmd === 'widget_open') return 'widget_open'
    if (cmd === 'executeWidgetMethod') return method === 'save' ? 'save' : 'widget_method'
    if (/cell/i.test(cmd || '')) return 'cell_open'
    if (cmd === 'copyPaste') return 'other'
    return 'other'
}

function eventTitle(cmd, method, WIDGET) {
    const wid = WIDGET?.id ? ` [${WIDGET.id}]` : ''
    const wtitle = WIDGET?.record?.title ? ` «${WIDGET.record.title}»` : ''
    if (cmd === 'widget_open') return `Open widget${wid}${wtitle}`
    if (cmd === 'executeWidgetMethod') {
        if (method === 'save') return `Save${wid}${wtitle}`
        if (method === 'select') return `Update data${wid}`
        return `Command ${method}${wid}`
    }
    return `${cmd} ${method || ''}${wid}`.trim()
}

// компактная копия action для воспроизведения (без тяжёлого WIDGET/view/record)
function slimAction(action) {
    return safe(() => {
        const a = {}
        const KEEP = ['command', 'method', 'widget', 'widget_class', 'binding', 'new',
            'params', 'props', 'item', 'get_currentRow', 'get_currentRow_with_meta', 'get_selectedRow',
            'method2', 'value', 'fieldsMapping', 'area', 'record_sendToBackend_exclude']
        for (const k of KEEP) if (action[k] !== undefined) a[k] = action[k]
        return a
    }) || { command: action?.command, method: action?.method }
}

// --- запись ------------------------------------------------------------------

export function start(store, opts = {}) {
    safe(() => {
        const rec = store.testRecording
        rec.active = true
        rec.t0 = performance.now()
        rec.events = []
        rec._pendingIndex = -1
        rec.targetWidgetId = opts.targetWidgetId || findTestWidget(store)?.id || null
        rec.captureScreens = opts.captureScreens !== false
        rec.env = snapshotEnv(store)
        // IE 2026 08 28 / ODOO-5828 (приёмка Игоря): параллельно с events собираем doc.steps
        // для записи сегмента TEST-DATA (test_type scenario-run)
        rec.steps = [{ n: 1, action: 'goto_page', page: rec.env.page || '', widget: '', row_key: '', row_hint: '',
            col: '', col_title: '', value: '', snapshot_name: '', save_after: false, use: true,
            comment: 'Environment: workspace ' + (rec.env.workspace || '') }]
        rec._lastPage = rec.env.page || ''
        // первое событие — окружение (нужно открыть перед прогоном)
        rec.events.push({
            n: 1, event_type: 'env',
            title: `Environment: page ${rec.env.page || '—'} / ws ${rec.env.workspace || '—'}`,
            action: { command: '_env' }, env: rec.env,
            record_id: '', record_segment: '', record_version: '',
            t_ms: 0, dur_ms: 0, console: '', notification: '',
            enabled: true, screenshot: '', db_changes: '', t2_ms: null, run_status: '',
        })
        store.show_notification?.({ text: t('● Test recording started'), status: 'pending', visible: true })   // IE 2026 09 23 / ODOO-341407: i18n
    })
}

// вызывается из executeStoreMethod ДО диспетчеризации действия
export function onAction(store, WIDGET, action) {
    safe(() => {
        const rec = store.testRecording
        if (!rec?.active) return
        const cmd = action?.command || ''
        if (cmd.startsWith('test_')) return                 // наши же команды
        // системные команды-ответы бэка и сохранение самой записи теста — не события
        if (['updateDoc', 'updateWidget', 'onSaveRecord__Click'].includes(cmd)) return
        if (WIDGET?.id && WIDGET.id === rec.targetWidgetId) return  // действия на самом элементе теста
        const n = rec.events.length + 1
        rec.events.push({
            n, event_type: eventType(cmd, action.method),
            title: eventTitle(cmd, action.method, WIDGET),
            command: cmd, method: action.method || '', widget: WIDGET?.id || '',
            // элемент (запись), на котором произошло действие — для колонок
            // ID/Segment/Version (AttrLink с relation_*_binding, Alt-click открывает)
            record_id: WIDGET?.record?.id || '', record_segment: WIDGET?.record?.segment || '',
            record_version: WIDGET?.record?.version || '',
            action: slimAction(action),
            t_ms: Math.round(performance.now() - rec.t0),
            dur_ms: null, console: '', notification: '',
            enabled: true, screenshot: '', db_changes: '', t2_ms: null, run_status: '',
        })
        rec._pendingIndex = rec.events.length - 1
        rec._pendingWidgetId = WIDGET?.id || ''
        captureStep(store, rec, WIDGET, action)
    })
}

// IE 2026 08 28 / ODOO-5828 (приёмка Игоря): событие → шаг таблицы Steps записи TEST-DATA.
// What-if действия (ветки/смена ПМ) в шаги не пишутся; подряд идущие смены чипсов схлопываются
// в итоговое состояние; после открытия отчёта/списка сразу добавляется шаг snapshot
function stepRow(fields) {
    return { n: 0, action: '', page: '', widget: '', row: '', row_key: '', row_hint: '', col: '', col_title: '',
        value: '', snapshot_name: '', save_after: false, use: true, comment: '', ...fields }
}
function captureStep(store, rec, WIDGET, action) {
    safe(() => {
        if (!rec.steps) return
        const cmd = action?.command || ''
        const method = action?.method || ''
        if (cmd === 'set_process_model' || /branch|cow_/.test(method)) return // What-if — не шаги сценария
        // смена страницы по ходу записи
        const curPage = store.activeWORKSPACE?.active_page || ''
        if (curPage && curPage !== rec._lastPage) {
            rec.steps.push(stepRow({ action: 'goto_page', page: curPage }))
            rec._lastPage = curPage
        }
        if (cmd === 'widget_open') {
            const wid = action.widget || ''
            if (!wid || wid === 'widget-selectors') return
            if (wid === 'widget-record' || wid === 'widget-row') {
                rec.steps.push(stepRow({ action: 'open_widget', page: curPage, widget: wid,
                    row_key: action.item?.id || '', row_hint: action.item?.title || '',
                    comment: action.item?.segment || '' }))
            } else {
                rec.steps.push(stepRow({ action: 'open_widget', page: curPage, widget: wid }))
                // авто-снимок результата отчёта/списка (лишние Игорь выключит галкой use)
                rec.steps.push(stepRow({ action: 'snapshot', widget: wid, snapshot_name: wid }))
            }
            return
        }
        if (cmd === 'onApplySelector__Click') {
            // состояние чипсов снимается ПОСЛЕ применения (фронт кладёт строки в params стола)
            setTimeout(() => safe(() => {
                const WS = store.WORKSPACES?.[WIDGET?.workspace] || store.activeWORKSPACE
                const rows = (WS?.record?.doc?.params?.WHERE || []).filter(r => r && r.use !== false
                    && !String((r.leftValue || {}).value || '').startsWith('calc')
                    && !String((r.leftValue || {}).value || '').startsWith('selector_map')
                    && (r.attrs || []).some(a2 => (a2?.rightValue || {}).value))
                const state = rows.map(r => ({ binding: (r.leftValue || {}).value || '',
                    condition: r.condition || 'in',
                    values: (r.attrs || []).filter(a2 => (a2?.rightValue || {}).value)
                        .map(a2 => ({ value: a2.rightValue.value, valueStr: a2.rightValue.valueStr || '' })) }))
                const summary = state.map(c => ((c.binding || '') + ': '
                    + c.values.map(v => v.valueStr || v.value).join(', '))).join('; ')
                const last = rec.steps[rec.steps.length - 1]
                if (last && last.action === 'set_chips') {
                    last.value = JSON.stringify(state); last.comment = summary
                } else {
                    rec.steps.push(stepRow({ action: 'set_chips', page: curPage,
                        value: JSON.stringify(state), comment: summary }))
                }
            }), 700)
            return
        }
        if (cmd === 'executeWidgetMethod' && method === 'save') {
            // развернуть прямые правки из undoStack гридов виджета в edit_cell (до ухода save на бэк)
            // IE 2026 10 01 / ODOO-5800: шаги — по контракту раннера (mainStore.clientTest_scenarioRun, шаг save: список пишется своей
            // кнопкой Save и падает, если писать нечего): правка = итог по ячейке за вычетом вернувшихся к исходному, косвенных и
            // связанных (грид: corrChip_cells — то же, что уходит Save), строка — по ссылке из стека ($item: индексы плывут после
            // сортировки); Save без единой правки ничего не пишет — шаг записывается выключенным (use false) с причиной
            const wid = WIDGET?.widget || WIDGET?.record?.id || ''
            const vos = Object.values(WIDGET?.attrs_vueObj || {}).filter(vo => vo?.undoStack?.length && vo.flex)
            const edits = []
            for (const vo of vos) {
                const cells = typeof vo.corrChip_cells === 'function' ? safe(() => vo.corrChip_cells()) : null
                if (Array.isArray(cells)) {
                    for (const g of cells) {
                        const last = g.entries[g.entries.length - 1] || {}
                        edits.push({ vo, it: g.item || {}, col: g.binding, value: last.newVal })
                    }
                    continue
                }
                const byCell = new Map()
                for (const a of vo.undoStack) {
                    if (!a || a.ind || a.indirect) continue
                    if (a.irow == null || !a.col_binding) continue
                    byCell.set(vo.attr?.name + '|' + a.irow + '|' + a.col_binding,
                        { vo, it: a.$item || vo.flex.rows[a.irow]?.dataItem || {}, col: a.col_binding, value: a.newVal })
                }
                edits.push(...byCell.values())
            }
            for (const { vo, it, col, value } of edits) {
                const hint = String(it.title || it['doc.subject.title'] || it.subject || it.object || it.id || '').slice(0, 60)
                rec.steps.push(stepRow({ action: 'edit_cell', page: curPage, widget: wid,
                    row_key: it.id || '', row_hint: hint, col,
                    col_title: vo.attr_by_binding?.[col]?.title || '', value }))
            }
            rec.steps.push(stepRow({ action: 'save', page: curPage, widget: wid, use: edits.length > 0,
                comment: edits.length ? '' : 'Save without edited cells — nothing to write, step disabled' }))
        }
    })
}

// вызывается из TERMINAL() ПОСЛЕ ответа бэкенда — дописать консоль/тайминги/скрин
export function onResponse(store, WIDGET, answer) {
    safe(() => {
        const rec = store.testRecording
        if (!rec?.active) return
        const i = rec._pendingIndex
        if (i == null || i < 0 || !rec.events[i]) return
        if (rec._pendingWidgetId && WIDGET?.id && rec._pendingWidgetId !== WIDGET.id) return
        const ev = rec.events[i]
        // консоль/терминал виджета (последние ~1500 символов — полезный вывод бэка)
        const term = (WIDGET?.TERMINAL || '')
        ev.console = term.length > 1500 ? '…' + term.slice(-1500) : term
        // нотификация (статус)
        const nt = WIDGET?.notification
        if (nt && nt.text) ev.notification = `${nt.status || ''}: ${nt.text}`
        // тайминги
        const be = Number(answer?.BACKEND_duration) || 0
        ev.dur_ms = Math.round((be * 1000) || (ev.dur_ms || 0))
        // изменения БД (эвристика из ответа: наличие сохранённых записей)
        const savedCount = answer?.record?._meta?.count_save ?? answer?.count_save
        if (savedCount) ev.db_changes = `catalogs:${savedCount}`
        rec._pendingIndex = -1
        // скриншот ключевых событий (best-effort, асинхронно)
        if (rec.captureScreens && ['widget_open', 'save'].includes(ev.event_type)) {
            captureScreenshot(WIDGET).then(dataUri => { if (dataUri) ev.screenshot = dataUri }).catch(() => {})
        }
    })
}

// --- скриншот (html2canvas, ленивый импорт — не грузим в бандл на старте) -----
async function captureScreenshot(WIDGET) {
    try {
        const el = WIDGET?.vueObj?.$el || document.querySelector(`[data-widget-id="${WIDGET?.id}"]`)
        if (!el || !el.getBoundingClientRect) return ''
        const mod = await import(/* webpackChunkName: "html2canvas" */ 'html2canvas')
        const html2canvas = mod.default || mod
        const canvas = await html2canvas(el, { scale: 0.4, logging: false, useCORS: true, backgroundColor: '#fff' })
        return canvas.toDataURL('image/jpeg', 0.6)
    } catch (e) {
        return ''
    }
}

// --- остановка: записать в элемент и сохранить --------------------------------
export function stop(store) {
    return safe(() => {
        const rec = store.testRecording
        if (!rec?.active) return
        rec.active = false
        const w = (rec.targetWidgetId != null
            ? allOpenWidgets(store).find(x => String(x.id) === String(rec.targetWidgetId) && x.record?.segment === 'TEST-SCENARIO')
            : null) || findTestWidget(store)
        if (w?.record) {
            // запись стартовала с открытого элемента — дописываем в него
            w.record.doc = w.record.doc || {}
            w.record.doc.env = rec.env
            w.record.doc.events = rec.events
            if (w.record._meta) w.record._meta.modified = true
            if (w.vueObj) {
                // штатное сохранение записи (как кнопка Save)
                store.executeStoreMethod(w.vueObj, {
                    command: 'onSaveRecord__Click', params: { closeWindow: false },
                    record_sendToBackend_exclude: ['_doc'],
                })
            }
            store.show_notification?.({ text: t('■ Recording stopped: {events}. Saved.', { events: tn(rec.events.length, '{n} event', '{n} events') }), status: 'success', visible: true })   // IE 2026 09 23 / ODOO-341407: i18n
        } else {
            // элемента нет — создаём новую запись: открываем widget-record с
            // заполненным сценарием и автосохраняем штатным save
            createTestRecord(store, rec)
        }
    })
}

function createTestRecord(store, rec) {
    const now = new Date()
    const stamp = now.toISOString().slice(0, 16).replace('T', ' ')
    // IE 2026 08 28 / ODOO-5828 (приёмка Игоря): запись — в регистр тестов данных (TEST-DATA,
    // scenario-run) с таблицей Steps; старый TEST-SCENARIO не используем (и не ломаем — хост-путь ниже)
    const steps = (rec.steps || []).map((st, i) => ({ ...st, n: i + 1 }))
    const item = {
        segment: 'TEST-DATA',
        title: `Scenario ${stamp} (${rec.env?.page || ''})`,
        doc: {
            use: true, test_type: 'scenario-run', compare_mode: 'golden',
            workspace: rec.env?.workspace || '', page: rec.env?.page || '',
            process_model: store.process_model_record?.id || '',
            steps,
        },
        meta: { comment: 'Recorded in browser (' + (rec.events?.length || 0) + ' events)' },   // IE 2026 10 06 / ODOO-5800: комментарий записи — в meta.comment (форма TEST-DATA привязана к meta), не doc.comment
        _meta: { new: true, modified: true },
    }
    const vueObj = allOpenWidgets(store).find(x => x.vueObj)?.vueObj
    if (!vueObj) {
        store.show_notification?.({ text: t('■ Recording stopped, but no open widget to host the save — open a page and press Stop again.'), status: 'warning', visible: true })   // IE 2026 09 23 / ODOO-341407: i18n (тосты функции)
        return
    }
    store.executeStoreMethod(vueObj, { command: 'widget_open', widget: 'widget-record', item })
    // тихого сохранения нет: пользователь смотрит таблицу Steps и жмёт Save сам
    store.show_notification?.({ text: t('■ Recording stopped: «{title}», {steps}. Review the Steps table and press Save.', { title: item.title, steps: tn(item.doc.steps.length, '{n} step', '{n} steps') }), status: 'info', visible: true })
}

// --- воспроизведение ----------------------------------------------------------
export async function replay(store, WIDGET, { test = false } = {}) {
    const rec = store.testRecording
    // из меню приходит активный виджет — найдём настоящий элемент теста
    if (!(WIDGET?.record?.segment === 'TEST-SCENARIO')) WIDGET = findTestWidget(store) || WIDGET
    const record = WIDGET?.record
    const events = (record?.doc?.events || []).filter(e => e && e.enabled !== false && e.event_type !== 'env')
    if (!record || !events.length) {
        store.show_notification?.({ text: t('No events to replay'), status: 'warning', visible: true })   // IE 2026 09 23 / ODOO-341407: i18n (тосты функции; локальная t → t0)
        return
    }
    rec.replaying = true
    const started_at = new Date().toISOString().replace('T', ' ').replace('Z', '')
    store.show_notification?.({ text: t('▶ Replay: {events}', { events: tn(events.length, '{n} event', '{n} events') }) + (test ? ' ' + t('(test, with restore)') : ''), status: 'pending', visible: true })

    // 1. восстановить окружение
    await openEnv(store, record.doc?.env)

    // 2. воспроизвести события, замерить новое время
    for (const ev of events) {
        const t0 = performance.now()
        let status = 'ok'
        try {
            await dispatchEvent(store, ev)
            await sleep(400)  // дать фронту/беку отработать
        } catch (e) {
            status = 'error'; console.warn('[replay]', e)
        }
        ev.t2_ms = Math.round(performance.now() - t0)
        ev.run_status = status
    }
    const ended_at = new Date().toISOString().replace('T', ' ').replace('Z', '')

    // 3. метки прогона в записи (для отката по аудиту)
    record.doc.run = { started_at, ended_at, user: store.user?.id || '', execution_logs: [] }
    if (record._meta) record._meta.modified = true

    // 4. тестовый прогон → откат по аудиту
    if (test && WIDGET.vueObj) {
        store.executeStoreMethod(WIDGET.vueObj, {
            command: 'executeWidgetMethod', method: 'restore',
            record_sendToBackend_exclude: ['_doc'],
        })
        store.show_notification?.({ text: t('▶ Replay finished, audit restore applied'), status: 'success', visible: true })
    } else {
        store.show_notification?.({ text: t('▶ Replay finished'), status: 'success', visible: true })
    }
    rec.replaying = false
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)) }

async function openEnv(store, env) {
    if (!env) return
    safe(() => {
        // сменить страницу/workspace через route, если отличается от текущей
        const cur = snapshotEnv(store)
        if (env.page && env.page !== cur.page) {
            const base = window.location.hash.split('?')[0]
            window.location.hash = `${base}?process_model=${env.process_model || ''}&workspace=${env.workspace || ''}&page=${env.page || ''}`
        }
    })
    await sleep(1500)
}

async function dispatchEvent(store, ev) {
    const action = { ...(ev.action || {}) }
    if (!action.command || action.command === '_env') return
    // найти целевой vueObj: виджет по id среди всех открытых, иначе любой активный
    const all = allOpenWidgets(store)
    let vueObj = safe(() => all.find(w => String(w.id) === String(ev.widget))?.vueObj)
    if (!vueObj) vueObj = safe(() => all.find(w => w.vueObj)?.vueObj)  // для widget_open целевого ещё нет
    if (!vueObj) throw new Error('no vueObj for ' + ev.widget)
    store.executeStoreMethod(vueObj, action)
}

// открыть список тестов (сегмент TEST-SCENARIO)
export function openList(store) {
    safe(() => {
        const vueObj = allOpenWidgets(store).find(w => w.vueObj)?.vueObj || store.topbarVueObj
        if (vueObj) {
            // IE 2026 08 28 / ODOO-5828: список — регистр тестов данных (TEST-DATA)
            store.executeStoreMethod(vueObj, {
                command: 'widget_open', widget: 'widget-list',
                props: { id: 'TEST-DATA' },
                fieldsMapping: [
                    { leftValue: 'doc.params.segment', rightValue: 'TEST-DATA' },
                ],
            })
        } else {
            store.show_notification?.({ text: t('Open a page first, then the test list'), status: 'info', visible: true })   // IE 2026 09 23 / ODOO-341407: i18n
        }
    })
}
